import React, {Fragment, useState} from "react";
import {useSearchParams} from "react-router-dom";
import PayoutList from "../../components/event/PayoutList";
import MollieSettlementList from "../../components/event/MollieSettlementList";
import Box from "../../components/UI/Box";
import Button from "../../components/UI/Button";
import {getMollieSettlementsReport, getPayoutsReport} from "../../api/eventGroupApiClient";

export const PAYOUT_PROVIDER_STRIPE = 'stripe';
export const PAYOUT_PROVIDER_MOLLIE = 'mollie';

// Stripe and Mollie report payouts in different shapes, so each provider keeps
// its own list component and report endpoint. Everything around them - the date
// range and the export button - is shared, and the active tab lives in the URL
// so the view can be linked and survives a reload.
const PROVIDERS = [
    {id : PAYOUT_PROVIDER_STRIPE, name : 'Stripe'},
    {id : PAYOUT_PROVIDER_MOLLIE, name : 'Mollie'}
];

const PayoutsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [filterFromDate, setFilterFromDate] = useState(null);
    const [filterToDate, setFilterToDate] = useState(null);

    const requestedProvider = searchParams.get('provider');
    const provider = PROVIDERS.some((option) => option.id === requestedProvider)
        ? requestedProvider
        : PAYOUT_PROVIDER_STRIPE;

    const handleProviderClick = (nextProvider) => {
        setSearchParams({provider : nextProvider});
    };

    const handleDownloadReportButtonClick = async () => {
        if (!filterFromDate || !filterToDate) {
            alert('Please select the date range.');
            return;
        }

        // DD-MM-YYYY formátum
        const from = filterFromDate.split('-').reverse().join('-');
        const to = filterToDate.split('-').reverse().join('-');

        try {
            const blob = provider === PAYOUT_PROVIDER_MOLLIE
                ? await getMollieSettlementsReport(filterFromDate, filterToDate)
                : await getPayoutsReport(filterFromDate, filterToDate);

            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary <a> element to trigger the download
            const link = document.createElement('a');
            link.href = url;
            // Stripe keeps the filename it always had - the bookkeeping side may
            // rely on it - and Mollie follows its own backend naming.
            const fileName = provider === PAYOUT_PROVIDER_MOLLIE
                ? `mollie_settlement_report_${from}_${to}.csv`
                : `payouts_${from}_${to}.csv`;

            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
        catch (error) {
            console.error('Failed to download the report:', error.message);
            alert('Failed to download the report. Please try again.');
        }
    };

    return <Fragment>
        <Box>
            <div className="flex gap-2 border-b border-gray-200">
                {PROVIDERS.map((option) => (
                    <button
                        key={option.id}
                        type="button"
                        onClick={() => handleProviderClick(option.id)}
                        className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors hover:cursor-pointer ${
                            provider === option.id
                                ? 'border-pink-600 text-pink-600'
                                : 'border-transparent text-gray-500 hover:text-gray-900'
                        }`}
                    >
                        {option.name}
                    </button>
                ))}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 my-4">
                <div className="flex flex-wrap gap-3">
                    <input
                        type="date"
                        className="border p-2"
                        value={filterFromDate ?? ''}
                        onChange={(event) => setFilterFromDate(event.target.value)}
                    />
                    <input
                        type="date"
                        className="border p-2"
                        value={filterToDate ?? ''}
                        onChange={(event) => setFilterToDate(event.target.value)}
                    />
                </div>
                <Button title='Download report' handleClick={handleDownloadReportButtonClick}/>
            </div>
            {provider === PAYOUT_PROVIDER_MOLLIE
                ? <MollieSettlementList fromDate={filterFromDate} toDate={filterToDate}/>
                : <PayoutList fromDate={filterFromDate} toDate={filterToDate}/>}
        </Box>
    </Fragment>
}

export default PayoutsPage;
